import {
  ExternalCalendar,
  ExternalConnectionCalendarsCache,
  WriteBlockedKind,
} from '@griply/types';

import { DocumentSnapshot } from '../types/firestore';
import { firebaseTimestampToDate } from './firebase-timestamp-to-date';

const KNOWN_WRITE_BLOCKED_KINDS: WriteBlockedKind[] = [
  WriteBlockedKind.Access,
  WriteBlockedKind.NotACalendar,
  WriteBlockedKind.Deleted,
];

/*
  writeBlockedCalendars: { [calendarId]: kind } — calendars the backend permanently refuses to
  write to. A known kind passes through; an UNKNOWN string coerces to the generic Blocked so a
  future backend kind still renders "can't sync" instead of looking healthy (forward compat);
  null/malformed values mean "not blocked" and stay absent.
*/
const coerceWriteBlockedKind = (value: unknown): WriteBlockedKind | null => {
  if (typeof value !== 'string' || !value) {
    return null;
  }
  return KNOWN_WRITE_BLOCKED_KINDS.includes(value as WriteBlockedKind)
    ? (value as WriteBlockedKind)
    : WriteBlockedKind.Blocked;
};

const coerceCalendar = (value: unknown): ExternalCalendar | null => {
  if (!value || typeof value !== 'object') {
    return null;
  }
  const entry = value as Record<string, unknown>;
  if (typeof entry.id !== 'string' || !entry.id) {
    return null;
  }
  return {
    id: entry.id,
    name: typeof entry.name === 'string' ? entry.name : '',
    description: typeof entry.description === 'string' ? entry.description : '',
    color: typeof entry.color === 'string' ? entry.color : '',
    timeZone: typeof entry.timeZone === 'string' ? entry.timeZone : null,
    // absence carries meaning on ExternalCalendar — only pass real booleans through
    ...(typeof entry.canWrite === 'boolean'
      ? { canWrite: entry.canWrite }
      : {}),
    ...(typeof entry.supportsVideoCall === 'boolean'
      ? { supportsVideoCall: entry.supportsVideoCall }
      : {}),
  };
};

// shared by the converter AND the getCalendars callable — both legs feed the same query cache
export const coerceCalendars = (value: unknown): ExternalCalendar[] =>
  Array.isArray(value)
    ? value
        .map(coerceCalendar)
        .filter((calendar): calendar is ExternalCalendar => calendar !== null)
    : [];

// Fold the write-blocked map onto the calendar entries, so every consumer downstream reads one
// shape (`calendar.writeBlocked`) instead of threading a separate map through hooks and pickers.
// Absence carries meaning — only annotate entries the map actually blocks.
const annotateWriteBlocked = (
  calendars: ExternalCalendar[],
  writeBlockedCalendars: unknown,
): ExternalCalendar[] => {
  if (!writeBlockedCalendars || typeof writeBlockedCalendars !== 'object') {
    return calendars;
  }
  const map = writeBlockedCalendars as Record<string, unknown>;
  return calendars.map((calendar) => {
    const kind = coerceWriteBlockedKind(map[calendar.id]);
    return kind ? { ...calendar, writeBlocked: kind } : calendar;
  });
};

export const externalConnectionCalendarsConverter = {
  fromFirestore: (
    snapshot: DocumentSnapshot,
  ): ExternalConnectionCalendarsCache => {
    const data = (snapshot.data() ?? {}) as Record<string, unknown>;
    return {
      id: snapshot.id,
      calendars: annotateWriteBlocked(
        coerceCalendars(data.calendars),
        data.writeBlockedCalendars,
      ),
      updatedAt: data.updatedAt
        ? firebaseTimestampToDate(data.updatedAt as Date)
        : null,
    };
  },
  toFirestore: (cache: ExternalConnectionCalendarsCache) => cache,
};
